import React, { useEffect, useState } from 'react';
import MetaTags from '@/components/meta/MetaTags';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';
import PageLayout from '@/components/layout/PageLayout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { TrendingUp, ShoppingCart, Plus, Clock, Users, Sparkles, FileText, Trophy, Medal, CircleDollarSign } from 'lucide-react';
import DropsMarketplace from '@/components/drops/DropsMarketplace';

interface DropCollection {
  id: string;
  name: string | null;
  status: string;
  payment_status: string;
  total_cost_cents: number;
  total_value_cents?: number | null;
  created_at: string;
  updated_at: string;
  user_id: string;
  banner_url?: string | null;
  icon_url?: string | null;
  profiles?: {
    display_name?: string;
    username?: string;
  };
}

interface DropRarity {
  id: string;
  collection_id: string;
  rarity: 'common' | 'uncommon' | 'rare' | 'legendary';
  count: number;
  unit_value_cents: number;
  media_url?: string | null;
}

interface MarketplaceListing {
  id: string;
  drop_id: string;
  seller_id: string;
  price_cents: number;
  status: string;
  created_at: string;
  drops?: {
    collection_id: string;
    rarity: string;
    media_url?: string;
    drop_collections?: {
      name: string;
    };
  };
  profiles?: {
    username?: string;
    display_name?: string;
  };
}

const statusVariant = (status: string): 'outline' | 'default' | 'destructive' | 'secondary' => {
  switch (status) {
    case 'approved':
      return 'default';
    case 'rejected':
      return 'destructive';
    case 'pending_review':
      return 'outline';
    case 'draft':
    default:
      return 'secondary';
  }
};

const prettyStatus = (s: string) => ({
  draft: 'Draft',
  pending_review: 'Pending Review',
  approved: 'Approved',
  rejected: 'Rejected',
} as Record<string, string>)[s] || s;

interface CreatorSummary {
  collections: number;
  totalDrops: number;
  claimed: number;
  remaining: number;
  totalValueCents: number;
}

const DropsDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [collections, setCollections] = useState<DropCollection[]>([]);
  const [draftCollections, setDraftCollections] = useState<DropCollection[]>([]);
  const [rarities, setRarities] = useState<Record<string, DropRarity[]>>({});
  const [draftRarities, setDraftRarities] = useState<Record<string, DropRarity[]>>({});
  const [marketplace, setMarketplace] = useState<MarketplaceListing[]>([]);
  const [loading, setLoading] = useState(true);
  const [draftsLoading, setDraftsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('collections');
  const [myCollections, setMyCollections] = useState<DropCollection[]>([]);
  const [myRarities, setMyRarities] = useState<Record<string, DropRarity[]>>({});
  const [myStats, setMyStats] = useState<Record<string, { total_count: number; claimed_count: number; remaining_count: number; claim_percentage: number }>>({});
  const [myLoading, setMyLoading] = useState(true);
  const [mySummary, setMySummary] = useState<CreatorSummary>({
    collections: 0,
    totalDrops: 0,
    claimed: 0,
    remaining: 0,
    totalValueCents: 0,
  });

  const formatCurrency = (valueCents: number) => {
    return `$${(valueCents / 100).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  // Show success message if redirected after submission
  useEffect(() => {
    if (searchParams.get('submitted') === 'true') {
      toast({
        title: '✅ Drop collection submitted!',
        description: 'Your drop collection is pending admin review. You\'ll be notified once it\'s approved.',
      });
      // Remove the parameter from URL
      searchParams.delete('submitted');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, toast]);

  // Load all approved collections
  useEffect(() => {
    const loadCollections = async () => {
      const { data } = await supabase
        .from('drop_collections')
        .select(`
          *,
          profiles:user_id(display_name, username)
        `)
        .eq('status', 'approved')
        .order('created_at', { ascending: false })
        .limit(20);
      
      if (data) {
        setCollections(data as any);
        
        // Load rarities for each collection
        const rarityData: Record<string, DropRarity[]> = {};
        for (const collection of data) {
          const { data: collectionRarities } = await supabase
            .from('drop_rarities')
            .select('*')
            .eq('collection_id', collection.id);
          if (collectionRarities) {
            rarityData[collection.id] = collectionRarities as DropRarity[];
          }
        }
        setRarities(rarityData);
      }
      setLoading(false);
    };
    loadCollections();
  }, []);

  // Load user's draft collections
  useEffect(() => {
    const loadDrafts = async () => {
      if (!user?.id) {
        console.log('[Drafts] No user ID, skipping draft load');
        setDraftsLoading(false);
        return;
      }

      console.log('[Drafts] Loading drafts for user:', user.id);

      const { data, error } = await supabase
        .from('drop_collections')
        .select('*')
        .eq('user_id', user.id)
        .eq('status', 'draft')
        .order('updated_at', { ascending: false });
      
      if (error) {
        console.error('[Drafts] Error loading drafts:', error);
      }

      console.log('[Drafts] Found drafts:', data?.length || 0, data);
      
      if (data) {
        setDraftCollections(data as any);
        
        // Load rarities for each draft collection
        const rarityData: Record<string, DropRarity[]> = {};
        for (const collection of data) {
          const { data: collectionRarities, error: raritiesError } = await supabase
            .from('drop_rarities')
            .select('*')
            .eq('collection_id', collection.id);
          
          if (raritiesError) {
            console.error('[Drafts] Error loading rarities for', collection.id, raritiesError);
          }
          
          if (collectionRarities) {
            rarityData[collection.id] = collectionRarities as DropRarity[];
            console.log('[Drafts] Loaded rarities for', collection.name, ':', collectionRarities.length);
          }
        }
        setDraftRarities(rarityData);
      }
      setDraftsLoading(false);
    };
    loadDrafts();
  }, [user?.id, toast]);

  // Load only this user's approved collections with analytics
  useEffect(() => {
    const loadMine = async () => {
      if (!user?.id) { setMyLoading(false); return; }
      setMyLoading(true);
      const { data } = await supabase
        .from('drop_collections')
        .select(`
          *,
          profiles:user_id(display_name, username)
        `)
        .eq('user_id', user.id)
        .eq('status', 'approved')
        .order('created_at', { ascending: false });

      if (data) {
        setMyCollections(data as any);
        const rarityData: Record<string, DropRarity[]> = {};
        const statsData: Record<string, { total_count: number; claimed_count: number; remaining_count: number; claim_percentage: number }> = {};
        const summary: CreatorSummary = {
          collections: data.length,
          totalDrops: 0,
          claimed: 0,
          remaining: 0,
          totalValueCents: 0,
        };
        for (const collection of data) {
          const { data: collectionRarities } = await supabase
            .from('drop_rarities')
            .select('*')
            .eq('collection_id', collection.id);
          if (collectionRarities) {
            rarityData[collection.id] = collectionRarities as DropRarity[];
            summary.totalDrops += collectionRarities.reduce((sum, r) => sum + r.count, 0);
          }

          const { data: stat } = await supabase
            .rpc('get_drop_collection_stats', { p_collection_id: collection.id });
          if (stat && stat[0]) {
            statsData[collection.id] = stat[0] as any;
            summary.claimed += stat[0].claimed_count || 0;
            summary.remaining += stat[0].remaining_count || 0;
          }

          const collectionValueCents = (collection.total_value_cents ?? collection.total_cost_cents ?? 0);
          summary.totalValueCents += collectionValueCents;
        }
        setMyRarities(rarityData);
        setMyStats(statsData);
        setMySummary(summary);
      }
      setMyLoading(false);
    };
    loadMine();
  }, [user?.id]);

  // Load marketplace listings (placeholder - you'll need to create this table)
  useEffect(() => {
    const loadMarketplace = async () => {
      // Placeholder for marketplace listings
      // You'll need to create a marketplace_listings table
      setMarketplace([]);
    };
    loadMarketplace();
  }, []);

  const rarityColors = {
    common: { bg: 'bg-emerald-500/20', border: 'border-emerald-500/40', text: 'text-emerald-300', dot: 'bg-emerald-500' },
    uncommon: { bg: 'bg-blue-500/20', border: 'border-blue-500/40', text: 'text-blue-300', dot: 'bg-blue-500' },
    rare: { bg: 'bg-purple-500/20', border: 'border-purple-500/40', text: 'text-purple-300', dot: 'bg-purple-500' },
    legendary: { bg: 'bg-orange-500/20', border: 'border-orange-500/40', text: 'text-orange-300', dot: 'bg-orange-500' },
  };

  return (
    <PageLayout>
      <MetaTags
        title="Drops Dashboard"
        description="Explore the latest drops, manage your collections, and track performance."
      />

      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col min-h-full">
        <div className="border-b border-border/50 backdrop-blur supports-[backdrop-filter]:bg-background/80 sticky top-0 z-10">
          <div className="container mx-auto max-w-7xl px-4 py-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold tracking-tight">Drops Command Center</h1>
                <p className="text-muted-foreground mt-1">
                  Manage your drops, track performance, and discover what's trending.
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <Button onClick={() => navigate('/drops/create')} className="gap-2">
                  <Plus className="w-4 h-4" />
                  New Drop Collection
                </Button>
              </div>
            </div>

            <TabsList className="mt-6 w-full md:w-auto">
              <TabsTrigger value="collections" className="flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                Featured
              </TabsTrigger>
              <TabsTrigger value="mine" className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                My Collections
              </TabsTrigger>
              <TabsTrigger value="drafts" className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Drafts
              </TabsTrigger>
              <TabsTrigger value="marketplace" className="flex items-center gap-2">
                <ShoppingCart className="w-4 h-4" />
                Marketplace
              </TabsTrigger>
              <TabsTrigger value="trending" className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4" />
                Trending
              </TabsTrigger>
            </TabsList>
          </div>
        </div>

        <div className="container mx-auto max-w-7xl px-4 py-8 flex-1">
          {/* Collections Tab */}
          <TabsContent value="collections" className="mt-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-muted-foreground">Loading collections...</div>
              </div>
            ) : collections.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <Sparkles className="w-12 h-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No collections yet</h3>
                  <p className="text-sm text-muted-foreground mb-4">Be the first to create an amazing drop collection!</p>
                  <Button onClick={() => navigate('/drops/create')}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create Your First Drop
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {collections.map((collection) => {
                  const collectionRarities = rarities[collection.id] || [];
                  const totalDrops = collectionRarities.reduce((sum, r) => sum + r.count, 0);
                  const creatorName = collection.profiles?.display_name || collection.profiles?.username || 'Anonymous';

                  return (
                    <Card 
                      key={collection.id} 
                      className="group overflow-hidden hover:shadow-xl transition-all cursor-pointer border-2 hover:border-primary/50"
                      onClick={() => navigate(`/drops/${collection.id}`)}
                    >
                      {/* Banner */}
                      <div className="relative h-32 bg-gradient-to-br from-purple-500/20 to-pink-500/20 overflow-hidden">
                        {collection.banner_url && (
                          <img 
                            src={collection.banner_url} 
                            alt={collection.name || 'Drop banner'}
                            className="w-full h-full object-cover"
                          />
                        )}
                        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-background/80" />
                      </div>

                      <CardContent className="relative -mt-10 px-6 pb-6">
                        {/* Icon */}
                        <div className="w-20 h-20 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mb-4 shadow-lg border-4 border-background">
                          {collection.icon_url ? (
                            <img src={collection.icon_url} alt={collection.name || 'Icon'} className="w-full h-full object-cover rounded-lg" />
                          ) : (
                            <Sparkles className="w-10 h-10 text-white" />
                          )}
                        </div>

                        {/* Collection Info */}
                        <h3 className="text-xl font-bold mb-1">{collection.name || 'Untitled Collection'}</h3>
                        <p className="text-sm text-muted-foreground mb-4">by {creatorName}</p>

                        {/* Stats */}
                        <div className="flex items-center gap-4 mb-4 text-sm">
                          <div className="flex items-center gap-1">
                            <Sparkles className="w-4 h-4 text-purple-400" />
                            <span className="font-medium">{totalDrops}</span>
                            <span className="text-muted-foreground">drops</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Clock className="w-4 h-4 text-blue-400" />
                            <span className="text-muted-foreground">
                              {new Date(collection.created_at).toLocaleDateString()}
                            </span>
                          </div>
                        </div>

                        {/* Rarities */}
                        <div className="space-y-2">
                          <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Rarities</div>
                          <div className="flex flex-wrap gap-2">
                            {collectionRarities.map((rarity) => {
                              const color = rarityColors[rarity.rarity];
                              return (
                                <div
                                  key={rarity.id}
                                  className={`flex items-center gap-2 px-3 py-1.5 rounded-full border ${color.border} ${color.bg}`}
                                >
                                  <div className={`w-2 h-2 rounded-full ${color.dot}`} />
                                  <span className={`text-xs font-medium ${color.text} capitalize`}>
                                    {rarity.rarity}
                                  </span>
                                  <span className="text-xs text-white/60">×{rarity.count}</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* My Collections Tab */}
          <TabsContent value="mine" className="mt-0">
            {myLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-muted-foreground">Loading your collections...</div>
              </div>
            ) : myCollections.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <Sparkles className="w-12 h-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">You don't have any approved collections yet</h3>
                  <p className="text-sm text-muted-foreground mb-4">Create a drop collection to see analytics here</p>
                  <Button onClick={() => navigate('/drops/create')}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create Drop
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {myCollections.map((collection) => {
                  const collectionRarities = myRarities[collection.id] || [];
                  const totalDrops = collectionRarities.reduce((sum, r) => sum + r.count, 0);
                  const creatorName = collection.profiles?.display_name || collection.profiles?.username || 'You';
                  const stat = myStats[collection.id];
                  const claimedPct = Math.max(0, Math.min(100, stat?.claim_percentage || 0));

                  return (
                    <Card 
                      key={collection.id} 
                      className="group overflow-hidden hover:shadow-xl transition-all cursor-pointer border-2 hover:border-primary/50"
                      onClick={() => navigate(`/drops/${collection.id}`)}
                    >
                      {/* Banner */}
                      <div className="relative h-32 bg-gradient-to-br from-purple-500/20 to-pink-500/20 overflow-hidden">
                        {collection.banner_url && (
                          <img 
                            src={collection.banner_url} 
                            alt={collection.name || 'Drop banner'}
                            className="w-full h-full object-cover"
                          />
                        )}
                        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-background/80" />
                      </div>

                      <CardContent className="relative -mt-10 px-6 pb-6">
                        {/* Icon */}
                        <div className="w-20 h-20 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mb-4 shadow-lg border-4 border-background">
                          {collection.icon_url ? (
                            <img src={collection.icon_url} alt={collection.name || 'Icon'} className="w-full h-full object-cover rounded-lg" />
                          ) : (
                            <Sparkles className="w-10 h-10 text-white" />
                          )}
                        </div>

                        {/* Collection Info */}
                        <h3 className="text-xl font-bold mb-1">{collection.name || 'Untitled Collection'}</h3>
                        <p className="text-sm text-muted-foreground mb-4">by {creatorName}</p>

                        {/* Stats */}
                        <div className="flex items-center gap-4 mb-3 text-sm">
                          <div className="flex items-center gap-1">
                            <Sparkles className="w-4 h-4 text-purple-400" />
                            <span className="font-medium">{totalDrops}</span>
                            <span className="text-muted-foreground">drops</span>
                          </div>
                          {stat && (
                            <div className="flex items-center gap-2 text-xs">
                              <span className="text-muted-foreground">Claimed:</span>
                              <span className="font-medium">{stat.claimed_count}/{stat.total_count}</span>
                              <span className="opacity-60">({claimedPct.toFixed(0)}%)</span>
                            </div>
                          )}
                        </div>

                        {/* Claim Progress */}
                        {stat && (
                          <div className="mb-4">
                            <Progress value={claimedPct} />
                            <div className="text-xs text-muted-foreground mt-1">
                              {stat.remaining_count} remaining
                            </div>
                          </div>
                        )}

                        {/* Rarities */}
                        {collectionRarities.length > 0 && (
                          <div className="space-y-2">
                            <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Rarities</div>
                            <div className="flex flex-wrap gap-2">
                              {collectionRarities.map((rarity) => (
                                <div
                                  key={rarity.id}
                                  className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-white/5"
                                >
                                  <div className="w-2 h-2 rounded-full bg-white/60" />
                                  <span className="text-xs font-medium capitalize">
                                    {rarity.rarity}
                                  </span>
                                  <span className="text-xs text-white/60">×{rarity.count}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Quick Actions */}
                        <div className="flex gap-3 mt-4">
                          <Button size="sm" onClick={(e) => { e.stopPropagation(); navigate(`/drops/${collection.id}`); }}>View</Button>
                          <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); navigate(`/drops/${collection.id}/claims`); }}>Claims</Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* My Drafts Tab */}
          <TabsContent value="drafts" className="mt-0">
            {draftsLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-muted-foreground">Loading drafts...</div>
              </div>
            ) : draftCollections.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <FileText className="w-12 h-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No drafts</h3>
                  <p className="text-sm text-muted-foreground mb-4">Your draft collections will appear here</p>
                  <Button onClick={() => navigate('/drops/create')}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create New Drop
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                  <p className="text-sm text-yellow-600 dark:text-yellow-400">
                    <strong>Draft collections</strong> are only visible to you. Complete and submit them for review to make them public.
                  </p>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {draftCollections.map((collection) => {
                    const collectionRarities = draftRarities[collection.id] || [];
                    const totalDrops = collectionRarities.reduce((sum, r) => sum + r.count, 0);

                    return (
                      <Card 
                        key={collection.id} 
                        className="group overflow-hidden hover:shadow-xl transition-all cursor-pointer border-2 border-yellow-500/30 hover:border-yellow-500/50"
                        onClick={() => navigate(`/drops/create?collectionId=${collection.id}`)}
                      >
                        {/* Banner */}
                        <div className="relative h-32 bg-gradient-to-br from-yellow-500/20 to-orange-500/20 overflow-hidden">
                          {collection.banner_url && (
                            <img 
                              src={collection.banner_url} 
                              alt={collection.name || 'Drop banner'}
                              className="w-full h-full object-cover opacity-50"
                            />
                          )}
                          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-background/80" />
                          <Badge className="absolute top-2 right-2 bg-yellow-500/20 text-yellow-600 border-yellow-500/30">
                            Draft
                          </Badge>
                        </div>

                        <CardContent className="relative -mt-10 px-6 pb-6">
                          {/* Icon */}
                          <div className="w-20 h-20 rounded-xl bg-gradient-to-br from-yellow-500 to-orange-500 flex items-center justify-center mb-4 shadow-lg border-4 border-background">
                            {collection.icon_url ? (
                              <img src={collection.icon_url} alt={collection.name || 'Icon'} className="w-full h-full object-cover rounded-lg" />
                            ) : (
                              <FileText className="w-10 h-10 text-white" />
                            )}
                          </div>

                          {/* Collection Info */}
                          <h3 className="text-xl font-bold mb-1">{collection.name || 'Untitled Collection'}</h3>
                          <p className="text-sm text-muted-foreground mb-4">Your draft</p>

                          {/* Stats */}
                          <div className="flex items-center gap-4 mb-4 text-sm">
                            <div className="flex items-center gap-1">
                              <Sparkles className="w-4 h-4 text-yellow-400" />
                              <span className="font-medium">{totalDrops}</span>
                              <span className="text-muted-foreground">drops</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Clock className="w-4 h-4 text-blue-400" />
                              <span className="text-muted-foreground">
                                Updated {new Date(collection.updated_at).toLocaleDateString()}
                              </span>
                            </div>
                          </div>

                          {/* Rarities */}
                          {collectionRarities.length > 0 && (
                            <div className="space-y-2">
                              <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Rarities</div>
                              <div className="flex flex-wrap gap-2">
                                {collectionRarities.map((rarity) => {
                                  const color = rarityColors[rarity.rarity];
                                  return (
                                    <div
                                      key={rarity.id}
                                      className={`flex items-center gap-2 px-3 py-1.5 rounded-full border ${color.border} ${color.bg}`}
                                    >
                                      <div className={`w-2 h-2 rounded-full ${color.dot}`} />
                                      <span className={`text-xs font-medium ${color.text} capitalize`}>
                                        {rarity.rarity}
                                      </span>
                                      <span className="text-xs text-white/60">×{rarity.count}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          <Button 
                            className="w-full mt-4" 
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/drops/create?collectionId=${collection.id}`);
                            }}
                          >
                            Continue Editing
                          </Button>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            )}
          </TabsContent>

          {/* Marketplace Tab */}
          <TabsContent value="marketplace" className="mt-0">
            <DropsMarketplace />
          </TabsContent>

          {/* Trending Tab */}
          <TabsContent value="trending" className="mt-0">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  Trending Collections
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center py-12">
                  <TrendingUp className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No trending data yet</h3>
                  <p className="text-sm text-muted-foreground">
                    Check back later to see what's hot
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </div>
      </Tabs>
    </PageLayout>
  );
};

export default DropsDashboard;